import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { ClockIcon } from "@heroicons/react/24/outline";

import AssemblyFlow, {
  CompletedTodaySection,
  getCompletedVehicles,
} from "../../components/productionDashboard/AssemblyFlow";
import BayStationCard from "../../components/productionDashboard/BayStationCard";
import DueNextSection from "../../components/productionDashboard/DueNextSection";
import InProgressSessionsColumn from "../../components/productionDashboard/InProgressSessionsColumn";
import OverviewKpiCard from "../../components/productionDashboard/OverviewKpiCard";
import PartsRemovedKpiCard from "../../components/productionDashboard/PartsRemovedKpiCard";
import YardLocationCapacityCard from "../../components/productionDashboard/YardLocationCapacityCard";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useLocationGroups from "../../data/useLocationGroups";
import useProductionDashboardOverview from "../../data/useProductionDashboardOverview";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { DismantlingBayType } from "../../models/DismantlingBay";
import { ProductionDashboardView } from "../../models/ProductionDashboard";
import classNames from "../../utils/classNames";

const STORAGE_KEY = {
  view: "productionDashboard.view",
  hideStats: "productionDashboard.hideStats",
};

const readStoredView = (): ProductionDashboardView => {
  if (typeof window === "undefined") return "Line";
  const value = window.localStorage.getItem(STORAGE_KEY.view);
  return value === "Bays" ? "Bays" : "Line";
};

const readStoredHideStats = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STORAGE_KEY.hideStats) === "true";
};

export default function ProductionDashboard() {
  const { t } = useTranslation("production");
  const { setInfo } = usePageNameContext();
  const isTme = CheckFeatureFlag("TME");
  const [view, setView] = useState<ProductionDashboardView>(readStoredView);
  const [hideStats, setHideStats] = useState<boolean>(readStoredHideStats);
  const [now, setNow] = useState<DateTime>(DateTime.local());

  const bayType =
    view === "Line" ? DismantlingBayType.ProductionLine : DismantlingBayType.Bay;
  const { overview } = useProductionDashboardOverview(bayType);
  const data = overview.data?.data;
  const { availabilities: locationGroupAvailabilities } = useLocationGroups();

  useEffect(() => {
    setInfo({
      name: t("Production Dashboard"),
      desc: t("Live view of vehicles being dismantled across the main line and off line stations"),
    });
  }, [setInfo, t]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(DateTime.local()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY.view, view);
  }, [view]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY.hideStats, String(hideStats));
  }, [hideStats]);

  const bays = useMemo(() => data?.bays ?? [], [data]);
  const assemblies = useMemo(() => {
    const all = data?.assemblies ?? [];
    // TME customers don't use the Non-Assembly column, so drop it from the flow.
    return isTme ? all.filter((a) => a.name !== "Non-Assembly") : all;
  }, [data, isTme]);
  const inProgressTriages = useMemo(() => data?.inProgressTriages ?? [], [data]);
  const inProgressDepollutions = useMemo(() => data?.inProgressDepollutions ?? [], [data]);
  const kpis = data?.kpis;

  // Vehicles with an active triage or depollution timer are being worked elsewhere, so they
  // shouldn't be shown sitting in an assembly in the flow (they appear in the in-progress
  // triage/depollution columns instead).
  const busyVehicleIds = useMemo(() => {
    const ids = new Set<string>();
    inProgressTriages.forEach((s) => s.vehicleId && ids.add(s.vehicleId));
    inProgressDepollutions.forEach((s) => s.vehicleId && ids.add(s.vehicleId));
    return ids;
  }, [inProgressTriages, inProgressDepollutions]);

  // Single source of truth for "completed today" so the KPI card below matches
  // the count shown in the Completed Today section exactly.
  const completedToday = useMemo(
    () => getCompletedVehicles(bays, busyVehicleIds),
    [bays, busyVehicleIds]
  );

  return (
    <div className="mt-2 flex flex-col gap-4">
      {/* Header strip: clock + date + toggles */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-gray-200 rounded-md p-3">
        <div className="flex items-center gap-3">
          <ClockIcon className="h-7 w-7 text-gray-400" />
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              {t("Current Time")}
            </div>
            <div className="text-3xl font-semibold text-gray-900 tabular-nums leading-none">
              {now.toFormat("HH:mm:ss")}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="text-sm text-gray-600">{now.toFormat("cccc d LLLL yyyy")}</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md bg-gray-100 p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setView("Line")}
                className={classNames(
                  "px-2.5 py-1 rounded",
                  view === "Line" ? "bg-gray-900 text-white" : "text-gray-700"
                )}
              >
                {t("Line")}
              </button>
              <button
                type="button"
                onClick={() => setView("Bays")}
                className={classNames(
                  "px-2.5 py-1 rounded",
                  view === "Bays" ? "bg-gray-900 text-white" : "text-gray-700"
                )}
              >
                {t("Bays")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setHideStats((p) => !p)}
              className="px-2.5 py-1 rounded border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {hideStats ? t("Show stats") : t("Hide stats")}
            </button>
          </div>
        </div>
      </div>

      {overview.isLoading ? (
        <div className="flex-1 flex items-center justify-center bg-white border border-gray-200 rounded-md py-12">
          <LoadingWheel />
        </div>
      ) : (
        <>
          {/* Stats + yard location capacity side by side */}
          {!hideStats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
              {/* Stats: 2x2 grid in one column */}
              {kpis && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {view === "Line" ? (
                    <OverviewKpiCard
                      label={t("Lines In Use")}
                      value={`${kpis.linesInUse.used} / ${kpis.linesInUse.total}`}
                      footer={t("Production lines in use")}
                    />
                  ) : (
                    <OverviewKpiCard
                      label={t("Bays In Use")}
                      value={`${kpis.baysInUse.used} / ${kpis.baysInUse.total}`}
                      footer={t("Bays in use")}
                    />
                  )}
                  <OverviewKpiCard
                    label={t("Vehicles In Progress")}
                    value={kpis.vehiclesInProgress}
                    footer={t("Across all stations")}
                  />
                  <OverviewKpiCard
                    label={t("Vehicles Dismantled Today")}
                    value={completedToday.length}
                    footer={t("Completed jobs")}
                  />
                  <PartsRemovedKpiCard partsRemoved={kpis.partsRemoved} />
                </div>
              )}

              {/* Yard location capacity — click a group to see each location.
                  Wrapper is relative so the card can fill it absolutely, letting
                  the stats column drive the height while the card scrolls inside. */}
              <div className="relative min-h-0">
                <div className="lg:absolute lg:inset-0">
                  <YardLocationCapacityCard
                    locationGroups={locationGroupAvailabilities.data?.data ?? []}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Due next */}
          {bays.length > 0 && (
            <div>
              <div className="mb-2 flex items-baseline gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                  {t("Due Onto The Line Next")}
                </h2>
                <span className="text-[11px] text-gray-500">{t("Sourced from the Production Planner")}</span>
              </div>
              <DueNextSection bays={bays} />
            </div>
          )}

          {/* Assembly flow — render every bay/line, including empty ones */}
          {bays.length > 0 && assemblies.length > 0 && (
            <div>
              <div className="mb-2 flex items-baseline gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                  {t("Assembly Flow")}
                </h2>
                <span className="text-[11px] text-gray-500">
                  {t("Vehicles flow through assemblies left to right")}
                </span>
              </div>
              <AssemblyFlow bays={bays} assemblies={assemblies} now={now} busyVehicleIds={busyVehicleIds} />
            </div>
          )}

          {/* Station / Bay grid */}

          {view === "Bays" &&
            (bays.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-sm text-gray-500">
                {t("No dismantling bays configured.")}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-3">
                {bays.map((bay) => (
                  <BayStationCard key={bay.id} bay={bay} now={now} />
                ))}
              </div>
            )
            )}

          {/* In-progress Triage / Depollution timers (driven by JobAssemblySession) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <InProgressSessionsColumn
              title={t("Triage In Progress")}
              sessions={inProgressTriages}
              now={now}
              routePrefix="triage"
            />
            <InProgressSessionsColumn
              title={t("Depollution In Progress")}
              sessions={inProgressDepollutions}
              now={now}
              routePrefix="depollution"
            />
          </div>

          {/* Vehicles completed today, gathered from every line/bay */}
          {bays.length > 0 && (
            <CompletedTodaySection vehicles={completedToday} />
          )}
        </>
      )}
    </div>
  );
}
